import type { Client } from '@basehub/data/replicache/index'
import { useUserData } from '~/hooks-data/use-user-data'
import { makeFullNameFromClerkUser } from '@basehub/data/auth/utils/make-full-name-from-clerk-user'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/sotano-kit/primitives/tooltip'
import { BaseHubAvatar } from '../basehub-avatar'
import { TooltipContentProps } from '@radix-ui/react-tooltip'
import { clsx } from 'clsx'
import * as React from 'react'
import { useUserStore } from '~/app/_store/user-store'
import { Button } from '~/sotano-kit/primitives/button'

export function ClientAvatar({
  client,
  tooltipSide,
  avatarClassName,
  className,
  followId,
}: {
  client: Client
  className?: string
  tooltipSide?: TooltipContentProps['side']
  avatarClassName?: string
  followId?: string
}) {
  const { data } = useUserData(client.userId)
  const name = data ? makeFullNameFromClerkUser(data) : ''
  const borderWidth = React.useMemo(
    () => (window && window.devicePixelRatio === 1 ? 2 : 1.5),
    []
  )
  const isFollowing = useUserStore((s) => s.followingUserId) === client.userId

  const content = React.useMemo(() => {
    if (tooltipSide === 'bottom') {
      return (
        <>
          <span className="font-medium">{name}</span>
          <br />
          {isFollowing ? 'Stop following' : 'Click to follow'}
        </>
      )
    }
    return (
      <>
        {isFollowing ? 'Stop following ' : 'Follow '}
        <span className="font-medium">{name}</span>
      </>
    )
  }, [tooltipSide, isFollowing, name])

  return (
    <div className="inline-flex items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            unstyled
            data-follow-id={followId}
            style={{
              borderWidth,
              borderStyle: 'solid',
              ...(isFollowing ? { borderColor: client.color } : {}),
            }}
            className={clsx(
              'rounded-full hover:cursor-default presence-client-avatar border-dark-border-solid',
              className
            )}
            onClick={() => {
              useUserStore.setState((s) => {
                if (s.followingUserId === client.userId) {
                  return { followingUserId: null }
                } else {
                  return { followingUserId: client.userId }
                }
              })
            }}
          >
            <BaseHubAvatar
              size="fill"
              id={client.userId}
              name={name}
              className={clsx('select-none', avatarClassName)}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{content}</TooltipContent>
      </Tooltip>
    </div>
  )
}
